import { message, Modal } from 'antd';
import { useCallback, useEffect, useRef, useState } from 'react';

import { checkWorkSchedulePeriod } from '../../../api/workScheduleApi';

const defaultConflictMessage =
  'За выбранный период уже существует действующий график.';
const checkErrorMessage =
  'Не удалось проверить возможность создания графика за выбранный период.';

const isUnavailableWorkSchedulePeriod = (response) => {
  const state = String(response?.state || '').toLowerCase();
  const httpStatusCode = String(response?.httpStatusCode || '').toLowerCase();

  return (
    response?.success === false ||
    state === 'error' ||
    httpStatusCode === 'conflict' ||
    httpStatusCode === '409'
  );
};

const isValidMonth = (month) => Boolean(month?.isValid?.());

export const useWorkSchedulePeriodCheck = ({
  enabled,
  initialMonth,
  workScheduleId,
}) => {
  const requestIdRef = useRef(0);
  const selectionVersionRef = useRef(0);
  const [selection, setSelection] = useState(() => ({
    month: initialMonth,
    version: 0,
  }));
  const [checkState, setCheckState] = useState(() => ({
    isChecking: Boolean(
      enabled && workScheduleId && isValidMonth(initialMonth)
    ),
    isUnavailable: false,
    message: '',
  }));

  const handleMonthChange = useCallback(
    (month) => {
      selectionVersionRef.current += 1;
      setSelection({
        month,
        version: selectionVersionRef.current,
      });
      setCheckState({
        isChecking: Boolean(enabled && workScheduleId && isValidMonth(month)),
        isUnavailable: false,
        message: '',
      });
    },
    [enabled, workScheduleId]
  );

  useEffect(() => {
    const { month } = selection;

    if (!enabled || !workScheduleId || !isValidMonth(month)) {
      requestIdRef.current += 1;
      setCheckState({
        isChecking: false,
        isUnavailable: false,
        message: '',
      });
      return undefined;
    }

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setCheckState((prevState) => ({
      ...prevState,
      isChecking: true,
      isUnavailable: false,
      message: '',
    }));
    const setUnavailablePeriod = (response) => {
      const conflictMessage = response?.message || defaultConflictMessage;

      setCheckState({
        isChecking: false,
        isUnavailable: true,
        message: conflictMessage,
      });
      Modal.warning({
        title: 'Невозможно создать график по периоду',
        content: conflictMessage,
      });
    };

    checkWorkSchedulePeriod({
      workScheduleId,
      periodMonth: month.format('M'),
      periodYear: month.format('YYYY'),
    })
      .then((response) => {
        if (requestIdRef.current !== requestId) {
          return;
        }

        if (isUnavailableWorkSchedulePeriod(response)) {
          setUnavailablePeriod(response);
          return;
        }

        setCheckState({
          isChecking: false,
          isUnavailable: false,
          message: '',
        });
      })
      .catch((error) => {
        if (requestIdRef.current !== requestId) {
          return;
        }

        const response = error?.response?.data;

        if (isUnavailableWorkSchedulePeriod(response)) {
          setUnavailablePeriod(response);
          return;
        }

        setCheckState({
          isChecking: false,
          isUnavailable: true,
          message: checkErrorMessage,
        });
        message.error(checkErrorMessage);
      });

    return () => {
      if (requestIdRef.current === requestId) {
        requestIdRef.current += 1;
      }
    };
  }, [enabled, selection, workScheduleId]);

  return {
    ...checkState,
    onMonthChange: handleMonthChange,
  };
};
